import React from "react";

import { imageMimetypes } from "shared/constants";
import classNames from "shared/utils/classNames";

type AttachmentVariant = "light" | "dark";

interface AttachmentProps {
  url: string;
  fileName: string;
  mimetype: string;
  fileSize: number;
  variant?: AttachmentVariant;
  onRemoveAttachment?: () => void;
  isFullScreen?: boolean;
}

const Attachment: React.FC<AttachmentProps> = ({
  url,
  fileName,
  mimetype,
  fileSize,
  onRemoveAttachment,
  variant = "dark",
  isFullScreen = false,
}) => {
  const getFileSizeLabel = (): string => {
    const kb = fileSize / 1_024;
    return kb < 1_024 ? `${kb.toFixed(2)} KB` : `${(kb / 1_024).toFixed(2)} MB`;
  };

  const getFileExtension = (): string => {
    return mimetype?.split("/")[1] || "";
  };

  const isImage = imageMimetypes.includes(mimetype);

  const variantClasses = {
    container:
      variant === "light" ? "border-grey-50 bg-white" : "border-none bg-accent",
    text: variant === "light" ? "text-neutral-strong" : "text-white-50",
    subText: variant === "light" ? "text-grey-1100" : "text-white-50",
    fileIcon:
      variant === "light" ? "bg-grey-200 text-error" : "bg-white text-error",
    borderRadius: variant === "dark" || isFullScreen ? "rounded-l-[11px]" : "",
  };

  return (
    <div
      className={classNames(
        "flex w-full items-center rounded-xl border border-solid",
        variantClasses.container,
        variant === "dark" ? "p-0.5" : "",
      )}
    >
      {onRemoveAttachment && (
        <button
          className="h-[60px] appearance-none rounded-l-xl border-none bg-grey-300 px-2 py-0"
          type="button"
          onClick={onRemoveAttachment}
          aria-label="Remove attachment"
        >
          <i className="ti ti-x text-xl" />
        </button>
      )}
      <div className="flex grow gap-3">
        <div
          onClick={() => window.open(url, "_blank")}
          role="button"
          tabIndex={0}
        >
          {isImage ? (
            <div
              className={classNames(
                "h-[60px] w-[60px] cursor-pointer bg-cover bg-center bg-no-repeat",
                variantClasses.borderRadius,
              )}
              style={{ backgroundImage: `url('${url}')` }}
            />
          ) : (
            <div className="h-[60px] w-[60px] rounded-lg">
              <div
                className={classNames(
                  "flex h-full w-full cursor-pointer items-center justify-center",
                  variantClasses.fileIcon,
                  variantClasses.borderRadius,
                )}
              >
                <i className="ti ti-file-filled text-[32px]" />
              </div>
            </div>
          )}
        </div>
        <div
          className={classNames(
            "flex flex-col gap-1 py-2.5 pr-2.5 text-sm",
            variantClasses.text,
          )}
        >
          <div
            title={fileName}
            className={classNames(
              "max-w-[180px] truncate text-sm font-semibold",
            )}
          >
            {fileName}
          </div>
          <div
            className={classNames(
              "flex items-center gap-1.5 text-xs",
              variantClasses.subText,
            )}
          >
            {getFileSizeLabel()}
            <i className="ti ti-circle-filled text-[4px] text-grey-50" />
            <span className="uppercase">{getFileExtension()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Attachment;
